"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useState, type ReactNode } from "react";

import { useToast } from "@/components/providers/FetchToastProvider";

import { useEstimator } from "../../_context/EstimatorContext";
import { summarizeCity } from "../../_logic/city-summary";
import { CITY_PARAM, COMPARE_MAX_CITIES } from "../../_logic/constants";
import { rankCities } from "../../_logic/city-sort";
import type { CityEstimate } from "../../_logic/types";
import { canPriceFares } from "../../_logic/trip-dates";
import { CitySummaryRow } from "./CitySummaryRow";

interface CitySummaryListProps {
  cities: CityEstimate[];
  lowestCity: string | null;
  /** The planner is viewing group totals: rank and lead with them. */
  byTotal: boolean;
  /** Cities picked for the side-by-side table. */
  picked: string[];
  /** Picks or unpicks a city; the caller enforces nothing, the list checks the limit first. */
  onTogglePick: (city: string) => void;
  /** A city's full breakdown, for when its row opens. */
  renderDetail: (city: CityEstimate) => ReactNode;
}

/**
 * Many cities as a ranked list, cheapest first (per person, or by total in Total mode): one hairline-divided row each with the per-person range, the
 * group total and what is priced. Clicking a row picks it for the side-by-side table (up to
 * three); its chevron opens the breakdown in place, one at a time, linkable with `?city=`.
 * @param props - The cities, the lowest, the cost view, the picks and the breakdown renderer.
 * @returns The list.
 * @throws Never.
 */
export const CitySummaryList = ({
  cities,
  lowestCity,
  byTotal,
  picked,
  onTogglePick,
  renderDetail,
}: CitySummaryListProps) => {
  // Hooks
  const { workspace, estimate } = useEstimator();
  const { showToast } = useToast();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // States
  // Until the planner opens or closes a row, the cheapest city (or the only one) starts open.
  const [touched, setTouched] = useState(false);

  // Derived
  const autoOpen = cities.length === 1 ? cities[0].city : lowestCity;
  const open = searchParams?.get(CITY_PARAM) ?? (touched ? null : autoOpen);
  const priced = workspace.dates !== null;
  const faresPriceable = workspace.dates !== null && canPriceFares(workspace.dates);

  // Handlers
  const setOpen = (city: string | null) => {
    setTouched(true);
    const params = new URLSearchParams(searchParams?.toString());
    if (city) params.set(CITY_PARAM, city);
    else params.delete(CITY_PARAM);
    const query = params.toString();
    // History only: the open row is view state, so no server round trip.
    window.history.replaceState(null, "", query ? `${pathname}?${query}` : pathname);
  };

  return (
    <ul
      data-tour="estimator-comparison"
      className="flex list-none flex-col divide-y divide-gray-100 border-y border-gray-100 @container"
    >
      {rankCities(cities, byTotal).map((city, index) => {
        const selected = picked.includes(city.city);
        return (
          <CitySummaryRow
            key={city.city}
            city={city}
            summary={summarizeCity(city, estimate, faresPriceable)}
            view={{ rank: index + 1, lowest: city.city === lowestCity, priced, byTotal }}
            expand={{
              expanded: open === city.city,
              onToggle: () => setOpen(open === city.city ? null : city.city),
              detail: renderDetail(city),
            }}
            select={{
              selected,
              onSelect: () => {
                if (!selected && picked.length >= COMPARE_MAX_CITIES) {
                  showToast(`Compare up to ${COMPARE_MAX_CITIES} cities`, "warning");
                } else onTogglePick(city.city);
              },
            }}
          />
        );
      })}
    </ul>
  );
};
